'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

// The booking form pulls in react-hook-form, zod, the zod resolver, date-fns and the
// calendar/popover/select primitives — ~150 KiB of JS that is unused until a visitor
// actually scrolls down to the form. Loading it on demand keeps it off the critical path.
const BookingForm = dynamic(() => import('./BookingForm'), {
  loading: () => <BookingFormPlaceholder />,
});

function BookingFormPlaceholder() {
  return (
    <div
      className="flex justify-center items-center h-full min-h-[600px] bg-muted/20 animate-pulse rounded-lg glass-strong glow-primary"
      aria-hidden="true"
    />
  );
}

export default function BookingFormLazy() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Without IntersectionObserver, fall back to rendering immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      // Start fetching before the form is on screen so it is ready by the time it is,
      // including when the hero CTA jumps straight to #book_now.
      { rootMargin: '600px 0px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="order-2 lg:order-1">
      {shouldRender ? <BookingForm /> : <BookingFormPlaceholder />}
    </div>
  );
}
